import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Link, Typography, Badge, Box } from '@mui/material';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';

import { translate as t } from 'hooks/useTranslations';
import useUrlParams from 'hooks/useUrlParams';
import useDraftManuals from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManuals';
import useVerticals from 'data_sources/useVerticals';
import useManualTypes from 'data_sources/useManualTypes';
import useJurisdictions from 'data_sources/useJurisdictions';
import useRiskPoolOrganizations from 'views/App/Portal/LexID/RiskPoolSharing/_data_sources/useRiskPoolOrganizations';
import errorHelper from 'helpers/errorHelper';
import { DraftManualsContext } from '../context';
import { AppContext } from 'views/App/context';
import { ActionMenu } from 'components/_atoms';
import datetimeHelper from 'helpers/datetimeHelper';
import { DraftManualItem, ManualPermissions } from 'views/App/Portal/Policy/_types/manuals/draft';
import { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import DraftManualsPolicyPageToolbar from './_components/PolicyPageToolbar';
import PhxBadge from '_lex/_atoms/PhxBadge';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';
import ExportModal, { ExportType } from 'views/App/Portal/Policy/DraftManuals/Detail/ExportModal';

type DraftManualsUrlParams = {
  search?: string | null;
  verticalId?: string | null;
  sortOrder?: 'TITLE_ASCENDING' | 'TITLE_DESCENDING';
  orgId?: string | null;
  jurisdictionId?: string | null;
};

const defaultParams: DraftManualsUrlParams = {
  search: null,
  verticalId: null,
  sortOrder: 'TITLE_ASCENDING',
  orgId: null,
  jurisdictionId: null,
};

const columnHelper = createColumnHelper<DraftManualItem>();

const DraftManualsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { manualHasPermissions, setManualPermissions } = useContext(DraftManualsContext);
  const {
    currentProfile,
    currentOrganizationId,
    isLexipolStaff,
    hasPermission,
    hasAppPermission,
    userOrganizationId,
    isFeatureEnabled,
  } = useContext(AppContext);
  const [params, setParams] = useUrlParams(defaultParams, { storage: true });
  const [sortModel, setSortModel] = React.useState([
    { field: 'name', sort: params.sortOrder === 'TITLE_DESCENDING' ? 'desc' : 'asc' },
  ]);

  useEffect(() => {
    // Keep sortModel in sync with params.sortOrder
    setSortModel([{ field: 'name', sort: params.sortOrder === 'TITLE_DESCENDING' ? 'desc' : 'asc' }]);
  }, [params.sortOrder]);

  const updateParams = useCallback(
    (updatedParams: DraftManualsUrlParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const queryParams = useMemo(() => {
    const newParams: any = {
      ...params,
    };
    Object.keys(newParams).forEach((key) => {
      if (newParams[key] === null || newParams[key] === '') {
        delete newParams[key];
      }
    });

    if (currentProfile?.user?.orgId && currentOrganizationId !== currentProfile?.user?.orgId) {
      newParams.orgId = currentOrganizationId;
    }

    return newParams;
  }, [params, currentProfile?.user?.orgId, currentOrganizationId]);

  const manualsQuery = useDraftManuals(queryParams, {
    onSuccess: (data: any) => {
      updateManualPermissions(data);
    },
  });

  // Only fetch organizations and jurisdictions for risk pool users
  const isRiskPoolUser = currentProfile?.organization?.riskPoolEnabled;

  // Temporarily disabled - organizations endpoint not ready
  const { data: relatedOrgs } = useRiskPoolOrganizations(
    currentOrganizationId,
    { enabled: false } // TODO: Re-enable when organizations endpoint is ready: { enabled: !!isRiskPoolUser }
  );

  const { data: jurisdictions } = useJurisdictions(
    { enabled: !!isRiskPoolUser },
    (errors) => errorHelper.enqueueErrors(errors)
  );

  // Create filter options for organizations
  const organizationOptions = useMemo(() => {
    if (!relatedOrgs?.content) return [];
    return relatedOrgs.content.map((org) => ({
      value: org.id,
      label: org.name,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [relatedOrgs]);

  // Create filter options for jurisdictions
  const jurisdictionOptions = useMemo(() => {
    if (!jurisdictions) return [];
    return jurisdictions.map((jurisdiction) => ({
      value: jurisdiction.id,
      label: jurisdiction.name,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [jurisdictions]);

  const updateManualPermissions = useCallback(
    (data: any) => {
      const mPerms: ManualPermissions = {};
      if (data.pages?.length > 0 && data.pages[0].content) {
        (data.pages ? data.pages.flatMap((x: any) => x.content) : ([] as DraftManualItem[])).forEach(
          (m: DraftManualItem) => {
            mPerms[m.id] = (m.authorizations ?? []).map((x) => x.action).sort();
          }
        );
        setManualPermissions(mPerms);
      }
    },
    [setManualPermissions]
  );

  useEffect(() => {
    if (manualsQuery.data) {
      updateManualPermissions(manualsQuery.data);
    }
  }, [manualsQuery.data, updateManualPermissions]);

  const manuals = useMemo(
    () => (manualsQuery.data && manualsQuery.data.pages ? manualsQuery.data.pages.flatMap((x) => x.content) : []),
    [manualsQuery.data]
  );

  const verticalsQuery = useVerticals();
  const manualTypesQuery = useManualTypes();
  const { createAgencyManual } = useManageManual();

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState<ExportType>('manual');
  const [selectedManualForExport, setSelectedManualForExport] = useState<DraftManualItem | null>(null);

  const handleExportClick = (manual: DraftManualItem, type: ExportType) => {
    setSelectedManualForExport(manual);
    setExportType(type);
    setExportModalOpen(true);
  };

  const manualActions: (manual: DraftManualItem) => ActionMenuItemProps[] = (manual) => {
    const actions: ActionMenuItemProps[] = [];

    // Check if this is a global manual (has 'global' in the name, case insensitive)
    const isGlobalManual = manual.name.toLowerCase().includes('global');

    if (manualHasPermissions(manual.id, ['issuePolicy'])) {
      actions.push({
        key: `${manual.id}_publish_approved`,
        label: t('draftManuals.actions.publishApproved'),
        href: `./${manual.id}/publish-approved`,
      });
    }
    if (manualHasPermissions(manual.id, ['issuePolicy'])) {
      actions.push({
        key: `${manual.id}_ack_settings`,
        label: t('draftManuals.actions.acknowledgementSettings'),
        href: `./${manual.id}/acknowledgement-settings`,
      });
    }
    if (manualHasPermissions(manual.id, ['listGIQQuestions'])) {
      actions.push({
        key: `${manual.id}_giq`,
        label: t('draftManuals.actions.giq'),
        href: `./${manual.id}/giq`,
      });
    }
    if (manualHasPermissions(manual.id, ['listCRQQuestions'])) {
      actions.push({
        key: `${manual.id}_crq`,
        label: t('draftManuals.actions.crq'),
        href: `./${manual.id}/crq`,
      });
    }
    // TODO: add correct permissions for these action
    if (manualHasPermissions(manual.id, ['listCRQQuestions'])) {
      actions.push({
        key: `${manual.id}_properties`,
        label: t('draftManuals.actions.properties'),
        href: `./${manual.id}/manual-properties`,
      });
    }

    // Add Create Jurisdiction Manual action for global manuals and Lexipol staff
    if (isGlobalManual && isLexipolStaff && hasPermission('createLexipolManual', currentOrganizationId)) {
      actions.push({
        key: `${manual.id}_create_jurisdiction`,
        label: t('draftManuals.actions.createJurisdictionManual'),
        href: `./create-jurisdiction-manual/${manual.id}`,
      });
    }

    // Add export actions if feature is enabled
    if (isFeatureEnabled('export-draft-manuals')) {
      actions.push({
        key: `${manual.id}_export_manual`,
        label: `Export ${t('draftManuals.export.manual')}`,
        onClick: () => handleExportClick(manual, 'manual'),
      });
      actions.push({
        key: `${manual.id}_export_toc`,
        label: `Export ${t('draftManuals.export.tableOfContents')}`,
        onClick: () => handleExportClick(manual, 'tableOfContents'),
      });
    }

    return actions;
  };

  const getVerticalName = useCallback(
    (verticalId: string) => {
      if (verticalsQuery.data) {
        return verticalsQuery.data.find((v) => v.id === verticalId)?.name;
      }
    },
    [verticalsQuery.data]
  );

  const columns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('policy.manuals.properties.name'),
        flex: 2,
        display: 'flex',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;

          const hasPermission = manualHasPermissions(row.id, ['lockDraftManual']);

          if (hasPermission) {
            return (
              <Link href={`./${row.id}`}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 'semibold' }}
                >
                  {row.name}
                </Typography>
              </Link>
            );
          }
          return (
            <Typography
              variant='h6'
            >
              {row.name}
            </Typography>
          );
        },
      },
      ...(currentProfile?.organization?.riskPoolEnabled && {
        organization: {
          header: t('policy.manuals.properties.organization'),
          flex: 1.5,
          display: 'flex',
          renderer: (params: GridCellParams) => {
            const row = params.row;
            if (!row) return null;
            return <Typography>{row.organization?.name}</Typography>;
          },
        },
      }),
      verticalId: {
        header: t('policy.manuals.properties.vertical'),
        flex: 1,
        display: 'flex',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Typography>{getVerticalName(row.verticalId)}</Typography>;
        },
      },
      manualType: {
        header: t('policy.manuals.properties.manualType'),
        flex: 1,
        display: 'flex',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Typography>{row.manualType}</Typography>;
        },
      },
      publishedAt: {
        header: t('policy.manuals.properties.lastPublishedAt'),
        flex: 1,
        display: 'flex',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Typography>{datetimeHelper.formatDate(row.publishedAt)}</Typography>;
        },
      },
      policyDraftStates: {
        header: t('policy.manuals.properties.activity'),
        flex: 1,
        display: 'flex',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          let approvedCount = row.policyDraftStates.APPROVED || 0;
          if (row.prefaceDraftStates.APPROVED) approvedCount += row.prefaceDraftStates.APPROVED;
          return (
            <PhxBadge color={approvedCount > 0 ? 'success' : 'neutral'}>
              {approvedCount > 0 ? `Policies Approved: ${approvedCount}` : 'No Activity'}
            </PhxBadge>
          );
        },
      },
      actions: {
        header: '',
        type: 'actions',
        flex: 0.25,
        display: 'flex',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return (
            <ActionMenu
              id={`action_menu_${row.id}`}
              items={manualActions(row)}
            />
          );
        },
      },
    }),
    [t, getVerticalName, manualActions]
  );

  const gridViewQuery = useMemo(
    () => ({
      hasNextPage: manualsQuery.hasNextPage,
      isFetchingNextPage: manualsQuery.isFetchingNextPage,
      fetchNextPage: manualsQuery.fetchNextPage,
      isError: manualsQuery.isError,
      isLoading: manualsQuery.isLoading,
      error: manualsQuery.error,
      data: {
        pages:
          manualsQuery.data?.pages.map((page) => ({
            count: page.count,
            rows: page.content,
          })) || [],
      },
    }),
    [manualsQuery]
  );

  const handleSortModelChange = (model: any) => {
    setSortModel(model);
    if (model.length > 0) {
      const { field, sort } = model[0];
      if (field === 'name') {
        updateParams({ sortOrder: sort === 'desc' ? 'TITLE_DESCENDING' : 'TITLE_ASCENDING' });
      }
    }
  };

  const isFiltered = useMemo(() => {
    return params.search || params.verticalId || params.orgId || params.jurisdictionId;
  }, [params]);

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const handleCreateGlobalManual = () => {
    navigate('./create-global-manual');
  };

  const handleCreateAgencySupplementalManual = async () => {
    if (!currentOrganizationId) {
      enqueueSnackbar(t('errors.organizationNotFound'), { variant: 'error' });
      return;
    }

    const payload = {
      name: 'Supplemental Manual',
      targetOrgId: currentOrganizationId,
    };

    createAgencyManual(
      userOrganizationId || '',
      payload,
      (response) => {
        enqueueSnackbar(
          t('draftManuals.createAgencySupplementalManual.success', '', { manualName: 'Supplemental Manual' }),
          {
            variant: 'success',
          }
        );
        queryClient.invalidateQueries(['draft_manuals']);
      },
      (error) => {
        enqueueSnackbar(error.message || t('errors.createManualFailed'), { variant: 'error' });
      }
    );
  };

  const handleCreateAgencyManual = () => {
    navigate('/admin/policy/draft-manuals/create-agency-manual');
  };

  return (
    <CommonLayout
      headerProps={{
        title: t('policy.draftManuals.title'),
        subtitle: isRiskPoolUser
          ? t('policy.draftManuals.subtitleRiskPool')
          : t('policy.draftManuals.subtitle'),
        titleBadge:
          (manualsQuery.data?.pages[0]?.count ?? 0) > 0 ? (
            <Badge
              variant='inline'
              badgeContent={manualsQuery.data?.pages[0]?.count}
              color='neutral'
              sx={{ textTransform: 'uppercase' }}
            />
          ) : undefined,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <DraftManualsPolicyPageToolbar
          params={params}
          updateParams={updateParams}
          isFiltered={isFiltered}
          clearParams={clearParams}
          verticalsData={verticalsQuery.data}
          jurisdictionOptions={jurisdictionOptions}
          onCreateGlobalManual={handleCreateGlobalManual}
          onCreateAgencySupplementalManual={handleCreateAgencySupplementalManual}
          onCreateAgencyManual={handleCreateAgencyManual}
        />
        <GridSection
          columns={columns}
          data={manuals}
          query={gridViewQuery}
          sorting={sortModel}
          onSortModelChange={handleSortModelChange}
        />
      </Box>

      {isFeatureEnabled('export-draft-manuals') && (
        <ExportModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          exportType={exportType}
          externalManual={selectedManualForExport}
        />
      )}
    </CommonLayout>
  );
};

export default DraftManualsList;
